/* eslint-disable no-inline-comments */
import {
	compareAsc,
	endOfDay,
	endOfMonth,
	endOfTomorrow,
	parse,
	startOfDay,
} from 'date-fns';
import { format, OptionsWithTZ, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import { IChartPoint } from '@interfaces/kpi/kpiCommon';

import { ETimeAggregationTypes, TTimeAggregationTypes } from '@enums/common';

import { timezoneUTC } from '@constants/common';

export const startOfDayInMilliseconds = (date: number | Date): number => startOfDay(date).getTime();

export const startOfDayInUTC = (date: Date): number => {
	return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
};

export const endOfDayInMilliseconds = (date: number | Date): number => endOfDay(date).getTime();

export const endOfDayInUTC = (date: Date): number => {
	return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
};

export const endOfTomorrowInUTC = (): number => {
	const date = endOfTomorrow();

	return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
};

export const startOfHourInUTC = (date: Date): number => {
	return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), 0, 0, 0);
};

export const endOfHourInUTC = (date: Date): number => {
	return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), 59, 59, 999);
};

export const startOfMinuteInUTC = (date: Date): number => {
	return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), 0, 0);
};

export const endOfMinuteInUTC = (date: Date): number => {
	return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), 59, 999);
};

export const isValidDate = (date: any): boolean => {
	try {
		const newDate = new Date(date);

		return (date instanceof Date || typeof date === 'number') && !!newDate;
	} catch (e) {
		return false;
	}
};

export const formatDate = (date: Date | number, template = 'dd/MM/yyyy', options?: OptionsWithTZ): string => {
	if (!isValidDate(date)) return 'Invalid Date';

	return format(date, template, options);
};

export const parseStringToDateInMilliseconds = (dateStr: string, template = 'dd/MM/yyyy', options?: OptionsWithTZ) => {
	const date = parse(dateStr, template, new Date(), options);

	if (!isValidDate(date)) return;

	return date.getTime();
};

export const parseStringToDate = (dateStr: string, template = 'dd/MM/yyyy', options?: OptionsWithTZ) => {
	const date = parse(dateStr, template, new Date(), options);

	if (!isValidDate(date)) return;

	return zonedTimeToUtc(date, timezoneUTC);
};

export const dateFormattingKpi = ({
	date,
	timeAggregationType,
	organizationTimezone,
	separator = '/',
	isMultiDays = false,
	showHourWithDayMonth = false,
}: {
	date: number;
	timeAggregationType: TTimeAggregationTypes;
	organizationTimezone: string;
	separator?: string;
	isMultiDays?: boolean;
	showHourWithDayMonth?: boolean;
}): string | string[] => {
	const zonedTime = utcToZonedTime(date, organizationTimezone);

	if ((timeAggregationType === ETimeAggregationTypes.hours || timeAggregationType === ETimeAggregationTypes.interval)
		&& isMultiDays) {
		const hour = formatDate(zonedTime, timeAggregationType === ETimeAggregationTypes.interval ? 'HH:mm' : "H'h", { timeZone: organizationTimezone });
		const day = showHourWithDayMonth
			? formatDate(zonedTime, `dd${separator}MM`, { timeZone: organizationTimezone })
			: formatDate(zonedTime, `dd${separator}MM${separator}yyyy`, { timeZone: organizationTimezone });

		return [hour, day];
	}

	const template = () => {
		switch (timeAggregationType) {
			case ETimeAggregationTypes.seconds:
				return 'ss';
			case ETimeAggregationTypes.minutes:
				return 'HH:mm';
			case ETimeAggregationTypes.hours: {
				const hour = format(date, "H'h'");
				const day = format(date, 'dd/MM/yyyy');

				return isMultiDays ? `${hour} \n ${day}` : "HH'h'";
			}
			case ETimeAggregationTypes.days:
				return `dd${separator}MM`;
			case ETimeAggregationTypes.months:
				return `MM${separator}yyyy`;
			case ETimeAggregationTypes.hourMinutes:
			case ETimeAggregationTypes.interval:
				return 'HH:mm';
			case ETimeAggregationTypes.dayMonthYear:
				return `dd${separator}MM${separator}yyyy`;
			default:
				return `dd${separator}MM${separator}yyyy`;
		}
	};

	return formatDate(zonedTime, template(), { timeZone: organizationTimezone });
};

export const dateFormattingBreadcrumbs = ({
	date,
	timeAggregationType,
	organizationTimezone,
	isMultiDays,
	// chart get from drill down
	isDrilled,
}: {
	date: number[];
	timeAggregationType: TTimeAggregationTypes;
	organizationTimezone: string;
	isMultiDays: boolean;
	isDrilled: boolean;
}): string => {
	const zonedTimeFirst = utcToZonedTime(date[0], organizationTimezone);
	const zonedTimeSecond = utcToZonedTime(date[date.length - 1], organizationTimezone);
	const options = { timeZone: organizationTimezone };

	const format = () => {
		switch (timeAggregationType) {
			case ETimeAggregationTypes.seconds:
				return formatDate(zonedTimeFirst, 'dd/MM/yyyy', options)
					+ ` ${formatDate(zonedTimeFirst, 'H', options)}h` + ` ${formatDate(zonedTimeFirst, 'mm', options)}min`;
			case ETimeAggregationTypes.minutes:
				return formatDate(zonedTimeFirst, 'dd/MM/yyyy', options);
			case ETimeAggregationTypes.hours: {
				return isMultiDays && date.length > 1
					? `${formatDate(zonedTimeFirst, 'dd/MM/yyyy', options)} - ${formatDate(zonedTimeSecond, 'dd/MM/yyyy', options)}`
					: formatDate(zonedTimeFirst, 'dd/MM/yyyy', options);
			}
			case ETimeAggregationTypes.days:
				return isDrilled
					? formatDate(zonedTimeFirst, 'MM/yyyy', options)
					: `${formatDate(zonedTimeFirst, 'dd/MM/yyyy', options)} - ${formatDate(zonedTimeSecond, 'dd/MM/yyyy', options)}`;
			case ETimeAggregationTypes.months:
				return `${formatDate(zonedTimeFirst, 'MM/yyyy', options)} - ${formatDate(zonedTimeSecond, 'MM/yyyy', options)}`;
			default:
				return formatDate(zonedTimeFirst, 'dd/MM/yyyy', options);
		}
	};

	return format();
};

export const getDateByTimeAggregationType = ({
	date,
	timeAggregationType,
	isStartOfDate,
	chartEndTo,
}: {
	date: number; // Comes in UTC 0 timezone
	timeAggregationType: TTimeAggregationTypes;
	isStartOfDate: boolean;
	chartEndTo?: number; // Comes in UTC 0 timezone
}): number => {
	//add an offset to get the correct day or month
	// when using the utils from date-fns depending on where we are
	// (because date-fsn converts UTC 0 to local time, and we need to get the end of the correct month or day at UTC 0)
	const offsetForSelectedDateInMls = new Date(date).getTimezoneOffset() * 60000;
	const dateWithLocalOffset = new Date(date + offsetForSelectedDateInMls);

	switch (timeAggregationType) {
		case ETimeAggregationTypes.seconds: // from minutes
			return isStartOfDate ? date : endOfMinuteInUTC(dateWithLocalOffset);
		case ETimeAggregationTypes.minutes: // from hours
			return isStartOfDate ? date : endOfHourInUTC(dateWithLocalOffset);
		case ETimeAggregationTypes.hours: { // from days
			//add offset to get the correct day depending on where we are
			return isStartOfDate ? date : endOfDayInUTC(dateWithLocalOffset);
		}

		case ETimeAggregationTypes.days: { // from months
			let startDate = 0;
			let endDate = 0;

			if (isStartOfDate) {
				startDate = date; // each line has a start date for its period in UTC 0
			} else {
				//add offset to get the correct day depending on where we are
				const localEndOfMonth = endOfMonth(date + offsetForSelectedDateInMls);
				const endOfMonthInUTC = endOfDayInUTC(localEndOfMonth);

				endDate = endOfMonthInUTC;

				// check if it's the end of the graph
				//if endOfMonthInUTC is after chartEndTo
				if (!!chartEndTo && compareAsc(endOfMonthInUTC, chartEndTo) === 1) {
					endDate = chartEndTo;
				}
			}

			return isStartOfDate ? startDate : endDate;
		}
		default:
			return date;
	}
};

export const getDateWithoutOffset = (date: number): Date => {
	return new Date(date + new Date(date).getTimezoneOffset() * 60000);
};

export const prepareDateForRequest =
	(date: number | null, formatter: (date: Date) => number): number | null => {
		return date ? formatter(new Date(date)) : null;
	};

export const checkIsMultiDays = ({
	points,
	organizationTimezone,
}: {
	points: Array<IChartPoint>;
	organizationTimezone: string;
}): boolean => {
	if (points.length === 0) {
		return false;
	}

	const formatTemplate = 'yyyy-MM-dd';

	const firstDateUTC = utcToZonedTime(points[0].x, organizationTimezone);
	const lastDateUTC = utcToZonedTime(points[points.length - 1].x, organizationTimezone);

	const formattedFirst = format(firstDateUTC, formatTemplate, { timeZone: organizationTimezone });
	const formattedLast = format(lastDateUTC, formatTemplate, { timeZone: organizationTimezone });

	return formattedFirst !== formattedLast;
};
